import React, { useState } from 'react';
import { PenLine, FlaskConical, Puzzle, Rocket, LineChart, MessageCircle, MousePointer2, FileText, Mic, MessageSquare, ClipboardCheck, Zap, Database, GitBranch, Phone, Globe, Share2, BarChart2, FileSearch, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { howItWorksSteps, stepDetails } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

const stepIconMap = {
  PenLine: PenLine,
  FlaskConical: FlaskConical,
  Puzzle: Puzzle,
  Rocket: Rocket,
  LineChart: LineChart,
};

const featureIconMap = {
  MessageCircle: MessageCircle,
  MousePointerSquare: MousePointer2,
  FileText: FileText,
  Mic: Mic,
  MessageSquare: MessageSquare,
  ClipboardCheck: ClipboardCheck,
  Zap: Zap,
  Database: Database,
  GitBranch: GitBranch,
  Phone: Phone,
  Globe: Globe,
  Share2: Share2,
  BarChart2: BarChart2,
  FileSearch: FileSearch,
  TrendingUp: TrendingUp,
};

const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(1);
  const { t } = useLanguage();

  const howItWorksText = t('howItWorks');
  
  // Get translated step details or fallback to mock data
  const translatedStepDetails = howItWorksText?.stepDetails;
  const activeDetails = translatedStepDetails?.[activeStep] || stepDetails[activeStep];
  
  // Get translated step buttons or fallback to mock data
  const stepButtonsTranslated = howItWorksText?.stepButtons || howItWorksSteps;

  return (
    <section className="bg-[#0a0a0b] py-20">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-teal-400/10 text-teal-400 text-sm font-medium px-4 py-1.5 rounded-full">
            {t('common.simpleProcess') || 'Simple Process'}
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-white mt-6">
            {howItWorksText?.title || 'How it Works'}
          </h2>
          <p className="text-gray-400 mt-4">
            {howItWorksText?.subtitle || 'Create and deploy your Voice AI assistant in five simple steps'}
          </p>
        </div>

        {/* Steps Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          {Array.isArray(stepButtonsTranslated) && stepButtonsTranslated.map((step) => {
            const IconComponent = stepIconMap[step.icon];
            const isActive = activeStep === step.step;
            return (
              <button
                key={step.step}
                onClick={() => setActiveStep(step.step)}
                className={`flex flex-col items-center p-4 md:p-6 rounded-xl border transition-all duration-300 min-w-[140px] md:min-w-[180px] ${
                  isActive
                    ? 'bg-[#1a1a1f] border-teal-400 shadow-lg shadow-teal-400/10'
                    : 'bg-[#0d1117] border-gray-800 hover:border-gray-700'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium mb-3 ${
                  isActive ? 'bg-teal-400 text-black' : 'bg-gray-800 text-gray-400'
                }`}>
                  {step.step}
                </div>
                <div className={`w-10 h-10 flex items-center justify-center mb-2 ${
                  isActive ? 'text-teal-400' : 'text-gray-500'
                }`}>
                  {IconComponent && <IconComponent className="w-5 h-5" />}
                </div>
                <h4 className={`font-medium text-sm md:text-base ${isActive ? 'text-white' : 'text-gray-400'}`}>
                  {step.title}
                </h4>
                <p className="text-gray-500 text-xs mt-1 text-center hidden md:block">
                  {step.description}
                </p>
                <div className="mt-3">
                  {isActive ? (
                    <ChevronUp className="w-4 h-4 text-teal-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Step Details Card */}
        {activeDetails && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-8">
          <h3 className="text-2xl font-semibold text-white mb-2">
            {activeDetails.title}
          </h3>
          <p className="text-gray-400 mb-8">
            {activeDetails.subtitle}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(activeDetails.features || []).map((feature, index) => {
              const FeatureIcon = featureIconMap[feature.icon];
              return (
                <div
                  key={index}
                  className="bg-[#1a1a1f] border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-colors"
                >
                  <div className="w-10 h-10 bg-teal-400/10 rounded-lg flex items-center justify-center mb-4">
                    {FeatureIcon && <FeatureIcon className="w-5 h-5 text-teal-400" />}
                  </div>
                  <h4 className="text-white font-medium mb-2">{feature.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

export default HowItWorks;
